import { useState, useEffect, useRef } from "react";
import { updateKYC } from "../../services/apiService";
import {
  useAllKycDoc,
  useAllUsers,
} from "../../services/hooks/getSuperAdminApis.js";
import { TbFileDownload } from "react-icons/tb";
import { toast } from "react-toastify";
import { Pagination } from "react-bootstrap";
import Loader from "../utilComponents/loader";
import ShimmerLoader from "../utilComponents/shimmer";
import CustomDropdown from "../utilComponents/customDropDown.js";
import { sendMailToUser } from "../../services/adminApi.js";

const KYCUpdates = ({ searchQuery }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [loadingButtons, setLoadingButtons] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedUserForMail, setSelectedUserForMail] = useState(null);
  const [message, setMessage] = useState("");

  const itemsPerPage = 5;
  const buttonRef = useRef(null);

  const { data: allKycDocs, isLoading } = useAllKycDoc();
  const { data: allUsers } = useAllUsers();
  console.log("All KYC Docs:", allKycDocs);
  console.log("All Users:", allUsers);
  useEffect(() => {
    if (allUsers && allKycDocs) {
      const kycAccountIDs = allKycDocs.map((doc) => doc.AccountID);
      const filteredUsers = allUsers.filter((user) =>
        kycAccountIDs.includes(user.AccountID)
      );

      const mergedData = filteredUsers.map((user) => {
        const kycDoc = allKycDocs.find(
          (doc) => doc.AccountID === user.AccountID
        );
        return {
          ...user,
          kycDetails: kycDoc || null,
        };
      });

      setUsers(mergedData);
    }
  }, [allUsers, allKycDocs]);

  // handling page change in pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // handling send mail to user
  const handleSendMail = async (AccountID, Email, message) => {
    setError(null);
    setLoading(true);
    try {
      // Assuming you have a function to send mail
      await sendMailToUser(AccountID, Email, message);
      toast.success("Mail sent successfully");
    } catch (err) {
      toast.error("Error sending mail. Please try again.");
    } finally {
      setLoading(false);
      closePopup();
    }
  };

  // handling kyc update and sending it to api
  const handleKYCUpdate = async (accountID, updatedStatus) => {
    setError(null);
    setLoadingButtons((prev) => ({ ...prev, [accountID]: true }));

    try {
      await updateKYC(accountID, updatedStatus);
      toast.success("KYC status updated successfully");

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.AccountID === accountID
            ? { ...user, KYC_Status: updatedStatus }
            : user
        )
      );
    } catch (err) {
      toast.error("Error updating KYC status. Please try again.");
    } finally {
      setLoadingButtons((prev) => ({ ...prev, [accountID]: false }));
    }
  };

  // handling status change in dropdown and updating the state
  const handleStatusChange = (accountID, value) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.AccountID === accountID ? { ...user, KYC_Status: value } : user
      )
    );
  };

  const openModal = (image) => {
    setModalImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
  };

  const openPopup = (user) => {
    setSelectedUserForMail(user);
    setIsPopupOpen(true);
  };
  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedUserForMail(null);
  };


  // downloading CSV file with data
  const downloadCSV = () => {
  const headers = [
    "AccountID",
    "SubmittedAt",
    "Document Type",
    "Document URL",
    "Selfie URL",
    "Full Name",
    "DOB",
    "Address",
    "KYC Status",
    "Role",
    "Email"
  ];

  const escapeCSV = (text) => {
    if (text == null) return "N/A";
    const str = text.toString();
    // Escape double quotes and wrap in double quotes
    return `"${str.replace(/"/g, '""')}"`;
  };

  const rows = users.map((req) => [
    escapeCSV(req.AccountID),
    escapeCSV(new Date(req.kycDetails?.submittedAt).toLocaleString()),
    escapeCSV(req.kycDetails?.documentType || "N/A"),
    escapeCSV(req.kycDetails?.documentUrl || "N/A"),
    escapeCSV(req.kycDetails?.selfieUrl || "N/A"),
    escapeCSV(req.kycDetails?.fullName || "N/A"),
    escapeCSV(new Date(req.kycDetails?.dob).toLocaleDateString()),
    escapeCSV(req.kycDetails?.address || "N/A"),
    escapeCSV(req.KYC_Status || "Not Updated"),
    escapeCSV(req.Role || "N/A"),
    escapeCSV(req.Email || "N/A")
  ]);

  const csvContent = [headers.map(escapeCSV), ...rows]
    .map((row) => row.join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "kyc_requests.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  const filteredRequests = users.filter(
    (request) =>
      request.AccountID.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.KYC_Status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.Email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredRequests.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const pending = users.filter((user) => user.KYC_Status === "Pending").length;

  return (
    <>
      {loading && <Loader />}
      {isLoading && <Loader />}
      <div>
        <h2 className="text-primary">User List and KYC Updates</h2>

        <div className="csv">
          {pending === 0 ? (
            <p style={{ color: "green" }}>No Pending Requests</p>
          ) : (
            <p style={{ color: "red" }}>Total Pending Requests: {pending}</p>
          )}
          <button
            onClick={downloadCSV}
            className="downloadBtn"
            ref={buttonRef}
            title="Download CSV">
            <TbFileDownload />
          </button>
        </div>

        {error && <p style={{ color: "red" }}>{error}</p>}

        <div className="scrollable-table">
          <table>
            <thead>
              <tr>
                <th>Account ID</th>
                <th>SubmittedAt</th>
                <th>Document Type</th>
                <th>Document</th>
                <th>Selfie</th>
                <th>Data</th>
                <th>KYC Status</th>
                <th>Actions</th>
                <th>Mail to User</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(currentItems) && currentItems.length > 0 ? (
                currentItems.map((user) => (
                  <tr key={user.AccountID}>
                    <td>{user.AccountID}</td>
                    <td>
                      {user.kycDetails?.submittedAt
                        ? new Date(user.kycDetails.submittedAt).toLocaleString()
                        : "N/A"}
                    </td>
                    <td>{user.kycDetails?.documentType || "N/A"}</td>
                    <td>
                      {user.kycDetails?.documentUrl ? (
                        <img
                          src={user.kycDetails.documentUrl}
                          alt="Document"
                          style={{ width: "50px", cursor: "pointer" }}
                          onClick={() => openModal(user.kycDetails.documentUrl)}
                        />
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      {user.kycDetails?.selfieUrl ? (
                        <img
                          src={user.kycDetails.selfieUrl}
                          alt="Selfie"
                          style={{ width: "50px", cursor: "pointer" }}
                          onClick={() => openModal(user.kycDetails.selfieUrl)}
                        />
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      <ul style={{ listStyleType: "none" }}>
                        {user.kycDetails ? (
                          <small>
                            <li>
                              <strong>Name:</strong> {user.kycDetails.fullName}
                            </li>
                            <li>
                              <strong>DOB:</strong>{" "}
                              {new Date(
                                user.kycDetails.dob
                              ).toLocaleDateString()}
                            </li>
                            <li>
                              <strong>Address:</strong>{" "}
                              {user.kycDetails.address}
                            </li>
                          </small>
                        ) : (
                          <li>No Data</li>
                        )}
                      </ul>
                    </td>
                    <td>
                      <span>{user.KYC_Status || "Pending"}</span>
                    </td>
                    <td>
                      <div>
                        <CustomDropdown
                          options={["Pending", "Approved", "Rejected"]}
                          selected={user.KYC_Status}
                          onSelect={(value) =>
                            handleStatusChange(user.AccountID, value)
                          }
                        />
                      </div>
                      <button
                        onClick={() =>
                          handleKYCUpdate(user.AccountID, user.KYC_Status)
                        }
                        disabled={loadingButtons[user.AccountID]}>
                        {loadingButtons[user.AccountID]
                          ? "Updating..."
                          : "Update Status"}
                      </button>
                    </td>
                    <td>
                      <small className="text-success">{user.Email}</small>
                      <button
                        onClick={() => openPopup(user)}
                        className="downloadBtn">
                        Email
                      </button>
                    </td>
                  </tr>
                ))
              ) : isLoading ? (
                <tr>
                  <td className="history-td" colSpan="10">
                    <ShimmerLoader width="120%" height="30px" />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td className="history-td" colSpan="8">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination className="d-flex justify-content-center mt-3">
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i}
              active={currentPage === i + 1}
              onClick={() => handlePageChange(i + 1)}>
              {i + 1}
            </Pagination.Item>
          ))}
        </Pagination>

        {isModalOpen && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.modal}>
              <button style={modalStyles.closeButton} onClick={closeModal}>
                x
              </button>
              <img
                src={modalImage}
                alt="Modal"
                style={{ width: "100%", height: "85vh" }}
              />
            </div>
          </div>
        )}

        {isPopupOpen && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.modal}>
              <h5>
                <strong> Send Mail to User </strong>
              </h5>
              <hr />
              <h6>
                <strong>To,</strong> {selectedUserForMail.Email || "no email "}
              </h6>
              <textarea
                rows="4"
                cols="50"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter message"
                className="form-control mb-3"
              />
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={closePopup}>
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    handleSendMail(selectedUserForMail.AccountID, selectedUserForMail.Email, message)
                  }
                  disabled={loading}>
                  {loading ? (
                    <span>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"></span>{" "}
                      Sending...
                    </span>
                  ) : (
                    "Send Mail"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    maxWidth: "500px",
    width: "100%",
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    padding: "5px 10px",
    backgroundColor: "red",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
};

export default KYCUpdates;
